import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { Button as VButton, FormItem } from 'vnetwork-ui'
import {
    TrendingUp,
    AlertTriangle,
    Clock,
    Building2,
    Zap,
    Timer,
    Bell,
    Plus,
    Trash2,
    Users,
    Mail,
    MessageSquare,
    Globe,
    FileText,
    Shield,
    Award,
    ArrowLeft,
    Save,
} from 'lucide-react'

import SubHeaders from '@/components/Headers/SubHeaders'
import TextEditorCustomize from '@/components/TextEditorCustomize'
import Paper from '@/components/Paper'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    getSLATemplate,
    createSLATemplate,
    updateSLATemplate,
} from '@/modules/Service/services/sla'
import {
    SLATemplatePayload,
    SLATier,
    SLA_TIER_CONFIG,
} from '@/modules/Service/types/sla'
import { paths } from '@/utils/path'
import {
    TIMEZONE_OPTIONS,
    WORK_DAYS_OPTIONS,
    PRIORITY_OPTIONS,
} from '@/modules/Service/constants'

const TIER_OPTIONS: { value: SLATier; label: string; color: string }[] = [
    { value: 'bronze', label: 'Bronze (99.0%)', color: '#CD7F32' },
    { value: 'silver', label: 'Silver (99.5%)', color: '#C0C0C0' },
    { value: 'gold', label: 'Gold (99.9%)', color: '#FFD700' },
    { value: 'platinum', label: 'Platinum (99.99%)', color: '#E5E4E2' },
    { value: 'custom', label: 'Tuỳ chỉnh', color: '#6B7280' },
]

// Notification channel options with icons (extends base constants)
const NOTIFICATION_CHANNELS = [
    { value: 'email', label: 'Email', icon: Mail },
    { value: 'sms', label: 'SMS', icon: MessageSquare },
    { value: 'slack', label: 'Slack', icon: MessageSquare },
    { value: 'teams', label: 'Teams', icon: Users },
]

const SLATemplatePage = () => {
    const { t } = useTranslation('services')
    const navigate = useNavigate()
    const { id } = useParams()
    const templateId = id ? Number(id) : undefined
    const isEditing = !!templateId

    // Fetch template if editing
    const { data: templateData, isLoading } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SLA_TEMPLATES, templateId],
        queryFn: () => getSLATemplate(templateId!),
        enabled: isEditing,
    })

    const template = templateData?.result

    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { isDirty },
    } = useForm<SLATemplatePayload>({
        defaultValues: {
            template_name: '',
            template_tier: 'bronze',
            template_description: '',
            uptime_guarantee: 99.0,
            downtime_allowance_minutes: 438,
            default_response_time: 480,
            default_resolution_time: 1440,
            business_hours_only: true,
            business_hours_config: {
                timezone: 'Asia/Ho_Chi_Minh',
                work_days: [1, 2, 3, 4, 5],
                work_hours: { start: '08:00', end: '17:30' },
                include_weekends: false,
            },
            is_active: true,
            sla_rules: [],
            escalation_rules: [],
            custom_terms: [],
        },
    })

    // Field arrays
    const {
        fields: escalationFields,
        append: appendEscalation,
        remove: removeEscalation,
    } = useFieldArray({
        control,
        name: 'escalation_rules',
    })

    const {
        fields: customTermFields,
        append: appendCustomTerm,
        remove: removeCustomTerm,
    } = useFieldArray({
        control,
        name: 'custom_terms',
    })

    const selectedTier = watch('template_tier')
    const watchUptime = watch('uptime_guarantee')
    const watchDowntime = watch('downtime_allowance_minutes')
    const watchResponse = watch('default_response_time')
    const watchResolution = watch('default_resolution_time')
    const watchIncludeWeekends = watch('business_hours_config.include_weekends')

    // Apply tier defaults when tier changes (only for new templates)
    useEffect(() => {
        if (!isEditing && selectedTier && selectedTier !== 'custom') {
            const tierConfig = SLA_TIER_CONFIG[selectedTier]
            if (tierConfig) {
                setValue('uptime_guarantee', tierConfig.uptime)
                setValue(
                    'downtime_allowance_minutes',
                    Math.round((100 - tierConfig.uptime) * 438)
                )
                setValue(
                    'default_response_time',
                    tierConfig.responseTimeMinutes
                )
                setValue(
                    'default_resolution_time',
                    tierConfig.resolutionTimeMinutes
                )
            }
        }
    }, [selectedTier, isEditing, setValue])

    // Reset form when template data loads
    useEffect(() => {
        if (template) {
            reset({
                template_name: template.template_name,
                template_tier: template.template_tier,
                template_description: template.template_description || '',
                uptime_guarantee: template.uptime_guarantee,
                downtime_allowance_minutes: template.downtime_allowance_minutes,
                default_response_time: template.default_response_time,
                default_resolution_time: template.default_resolution_time,
                business_hours_only: template.business_hours_only,
                business_hours_config: template.business_hours_config || {
                    timezone: 'Asia/Ho_Chi_Minh',
                    work_days: [1, 2, 3, 4, 5],
                    work_hours: { start: '08:00', end: '17:30' },
                    include_weekends: false,
                },
                is_active: template.is_active,
                sla_rules: template.sla_rules || [],
                escalation_rules: template.escalation_rules || [],
                custom_terms: template.custom_terms || [],
            })
        }
    }, [template, reset])

    // Add new escalation rule
    const handleAddEscalation = () => {
        appendEscalation({
            condition: {
                ticket_type_group: 'default',
                priority: ['critical', 'high'],
                no_response_after_minutes: 60,
            },
            escalate_to: {
                employee_ids: [],
                roles: ['manager'],
            },
            notification_channels: ['email'],
            escalation_level: escalationFields.length + 1,
        })
    }

    // Create mutation
    const { mutate: create, isPending: isCreating } = useMutationWithHandler(
        {
            mutationFn: (payload: SLATemplatePayload) =>
                createSLATemplate(payload),
        },
        {
            successMessage: t('sla.template_created', 'Đã tạo template SLA'),
            reactQueryKeys: QUERY_KEYS.SLA_TEMPLATES,
            onSuccess: () => navigate(paths.slaTemplates),
        }
    )

    // Update mutation
    const { mutate: update, isPending: isUpdating } = useMutationWithHandler(
        {
            mutationFn: (payload: SLATemplatePayload) =>
                updateSLATemplate(templateId!, payload),
        },
        {
            successMessage: t(
                'sla.template_updated',
                'Đã cập nhật template SLA'
            ),
            reactQueryKeys: QUERY_KEYS.SLA_TEMPLATES,
            onSuccess: () => navigate(paths.slaTemplates),
        }
    )

    const isPending = isCreating || isUpdating

    const onSubmit = (data: SLATemplatePayload) => {
        const mutation = isEditing ? update : create
        mutation(data)
    }

    if (isLoading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-96 w-full" />
            </div>
        )
    }

    return (
        <div className="space-y-4">
            <SubHeaders
                actions={[]}
                breadcrumbs={[
                    {
                        path: paths.settings,
                        title: t('common.settings', 'Cài đặt'),
                    },
                    {
                        path: paths.slaTemplates,
                        title: t('sla.templates_title', 'SLA Templates'),
                    },
                    {
                        path: '#',
                        title: isEditing
                            ? t('sla.edit_template', 'Sửa Template')
                            : t('sla.create_template', 'Tạo Template'),
                    },
                ]}
            />

            <form onSubmit={handleSubmit(onSubmit)}>
                {/* Header Actions */}
                <Paper className="p-4 mb-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <Shield className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <h1 className="text-lg font-semibold">
                                    {isEditing
                                        ? t(
                                              'sla.edit_template',
                                              'Sửa Template SLA'
                                          )
                                        : t(
                                              'sla.create_template',
                                              'Tạo Template SLA'
                                          )}
                                </h1>
                                {template && (
                                    <p className="text-sm text-gray-500">
                                        {template.template_name}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate(paths.slaTemplates)}
                            >
                                <ArrowLeft className="w-4 h-4 mr-1" />
                                Quay lại
                            </Button>
                            <VButton
                                type="submit"
                                loading={isPending}
                                disabled={!isDirty && isEditing}
                            >
                                <Save className="w-4 h-4 mr-1" />
                                {isEditing ? 'Lưu thay đổi' : 'Tạo Template'}
                            </VButton>
                        </div>
                    </div>
                </Paper>

                {/* Basic Info */}
                <Card className="mb-2">
                    <CardHeader className="pb-3">
                        <div className="flex items-center gap-2">
                            <Award className="w-4 h-4 text-blue-500" />
                            <CardTitle className="text-sm">
                                Thông tin cơ bản
                            </CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                            <FormItem
                                control={control}
                                name="template_name"
                                type="textfield"
                                label={t('sla.template_name', 'Tên template')}
                                placeholder="VD: Gold SLA - Enterprise"
                                size="small"
                            />
                            <div className="space-y-1">
                                <Label className="text-xs">Tier</Label>
                                <Controller
                                    name="template_tier"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            value={field.value}
                                            onValueChange={field.onChange}
                                        >
                                            <SelectTrigger className="h-9">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {TIER_OPTIONS.map((tier) => (
                                                    <SelectItem
                                                        key={tier.value}
                                                        value={tier.value}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <div
                                                                className="w-3 h-3 rounded-full"
                                                                style={{
                                                                    backgroundColor:
                                                                        tier.color,
                                                                }}
                                                            />
                                                            {tier.label}
                                                        </div>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                            </div>
                        </div>
                        <FormItem
                            control={control}
                            name="template_description"
                            type="textarea"
                            label={t('sla.description', 'Mô tả')}
                            placeholder="Mô tả ngắn về template..."
                            size="small"
                        />
                    </CardContent>
                </Card>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                    {/* Left Column */}
                    <div className="space-y-2">
                        {/* SLA Metrics */}
                        <Card>
                            <CardHeader className="pb-3">
                                <div className="flex items-center gap-2">
                                    <TrendingUp className="w-4 h-4 text-green-500" />
                                    <CardTitle className="text-sm">
                                        Chỉ số SLA
                                    </CardTitle>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                <div className="grid grid-cols-2 gap-2">
                                    <div className="space-y-1">
                                        <Label className="text-xs flex items-center gap-1">
                                            <TrendingUp className="w-3 h-3 text-green-500" />
                                            Uptime (%)
                                        </Label>
                                        <Controller
                                            name="uptime_guarantee"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    {...field}
                                                    type="number"
                                                    step="0.01"
                                                    className="h-9"
                                                    onChange={(e) =>
                                                        field.onChange(
                                                            parseFloat(
                                                                e.target.value
                                                            )
                                                        )
                                                    }
                                                />
                                            )}
                                        />
                                        {watchUptime && (
                                            <p className="text-xs text-gray-500">
                                                ≈{' '}
                                                {(
                                                    (100 - (watchUptime || 0)) *
                                                    7.2
                                                ).toFixed(1)}{' '}
                                                giờ downtime/tháng
                                            </p>
                                        )}
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-xs flex items-center gap-1">
                                            <AlertTriangle className="w-3 h-3 text-orange-500" />
                                            Downtime (phút/tháng)
                                        </Label>
                                        <Controller
                                            name="downtime_allowance_minutes"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    {...field}
                                                    type="number"
                                                    className="h-9"
                                                    onChange={(e) =>
                                                        field.onChange(
                                                            parseInt(
                                                                e.target.value
                                                            )
                                                        )
                                                    }
                                                />
                                            )}
                                        />
                                        {watchDowntime && (
                                            <p className="text-xs text-gray-500">
                                                ≈{' '}
                                                {(
                                                    (watchDowntime || 0) / 60
                                                ).toFixed(1)}{' '}
                                                giờ/tháng
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-2 pt-2 border-t">
                                    <div className="space-y-1">
                                        <Label className="text-xs flex items-center gap-1">
                                            <Clock className="w-3 h-3 text-blue-500" />
                                            Phản hồi (phút)
                                        </Label>
                                        <Controller
                                            name="default_response_time"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    {...field}
                                                    type="number"
                                                    className="h-9"
                                                    onChange={(e) =>
                                                        field.onChange(
                                                            parseInt(
                                                                e.target.value
                                                            )
                                                        )
                                                    }
                                                />
                                            )}
                                        />
                                        {watchResponse &&
                                            watchResponse >= 60 && (
                                                <p className="text-xs text-gray-500">
                                                    ={' '}
                                                    {(
                                                        watchResponse / 60
                                                    ).toFixed(1)}{' '}
                                                    giờ
                                                </p>
                                            )}
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-xs flex items-center gap-1">
                                            <Timer className="w-3 h-3 text-purple-500" />
                                            Xử lý (phút)
                                        </Label>
                                        <Controller
                                            name="default_resolution_time"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    {...field}
                                                    type="number"
                                                    className="h-9"
                                                    onChange={(e) =>
                                                        field.onChange(
                                                            parseInt(
                                                                e.target.value
                                                            )
                                                        )
                                                    }
                                                />
                                            )}
                                        />
                                        {watchResolution &&
                                            watchResolution >= 60 && (
                                                <p className="text-xs text-gray-500">
                                                    ={' '}
                                                    {(
                                                        watchResolution / 60
                                                    ).toFixed(1)}{' '}
                                                    giờ
                                                </p>
                                            )}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Settings */}
                        <Card>
                            <CardHeader className="pb-3">
                                <div className="flex items-center gap-2">
                                    <Zap className="w-4 h-4 text-yellow-500" />
                                    <CardTitle className="text-sm">
                                        Cài đặt
                                    </CardTitle>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <Building2 className="w-4 h-4 text-blue-500" />
                                        <div>
                                            <Label className="text-sm">
                                                Chỉ tính giờ làm việc
                                            </Label>
                                            <p className="text-xs text-gray-500">
                                                SLA chỉ tính trong giờ hành
                                                chính
                                            </p>
                                        </div>
                                    </div>
                                    <Controller
                                        name="business_hours_only"
                                        control={control}
                                        render={({ field }) => (
                                            <Switch
                                                checked={field.value}
                                                onCheckedChange={field.onChange}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <Zap className="w-4 h-4 text-green-500" />
                                        <div>
                                            <Label className="text-sm">
                                                Kích hoạt Template
                                            </Label>
                                            <p className="text-xs text-gray-500">
                                                Cho phép sử dụng template này
                                            </p>
                                        </div>
                                    </div>
                                    <Controller
                                        name="is_active"
                                        control={control}
                                        render={({ field }) => (
                                            <Switch
                                                checked={field.value}
                                                onCheckedChange={field.onChange}
                                            />
                                        )}
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-2">
                        {/* Business Hours Config */}
                        <Card>
                            <CardHeader className="pb-3">
                                <div className="flex items-center gap-2">
                                    <Globe className="w-4 h-4 text-indigo-500" />
                                    <CardTitle className="text-sm">
                                        Cấu hình giờ làm việc
                                    </CardTitle>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                    <div>
                                        <Label className="text-sm">
                                            Tính 24/7
                                        </Label>
                                        <p className="text-xs text-gray-500">
                                            SLA được tính liên tục
                                        </p>
                                    </div>
                                    <Controller
                                        name="business_hours_config.include_weekends"
                                        control={control}
                                        render={({ field }) => (
                                            <Switch
                                                checked={field.value}
                                                onCheckedChange={field.onChange}
                                            />
                                        )}
                                    />
                                </div>
                                {!watchIncludeWeekends && (
                                    <>
                                        <div className="space-y-1">
                                            <Label className="text-xs">
                                                Múi giờ
                                            </Label>
                                            <Controller
                                                name="business_hours_config.timezone"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        value={field.value}
                                                        onValueChange={
                                                            field.onChange
                                                        }
                                                    >
                                                        <SelectTrigger className="h-9">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {TIMEZONE_OPTIONS.map(
                                                                (tz) => (
                                                                    <SelectItem
                                                                        key={
                                                                            tz.value
                                                                        }
                                                                        value={
                                                                            tz.value
                                                                        }
                                                                    >
                                                                        {
                                                                            tz.label
                                                                        }
                                                                    </SelectItem>
                                                                )
                                                            )}
                                                        </SelectContent>
                                                    </Select>
                                                )}
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div className="space-y-1">
                                                <Label className="text-xs">
                                                    Giờ bắt đầu
                                                </Label>
                                                <Controller
                                                    name="business_hours_config.work_hours.start"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            {...field}
                                                            type="time"
                                                            className="h-9"
                                                        />
                                                    )}
                                                />
                                            </div>
                                            <div className="space-y-1">
                                                <Label className="text-xs">
                                                    Giờ kết thúc
                                                </Label>
                                                <Controller
                                                    name="business_hours_config.work_hours.end"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            {...field}
                                                            type="time"
                                                            className="h-9"
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs">
                                                Ngày làm việc
                                            </Label>
                                            <Controller
                                                name="business_hours_config.work_days"
                                                control={control}
                                                render={({ field }) => (
                                                    <div className="flex flex-wrap gap-1">
                                                        {WORK_DAYS_OPTIONS.map(
                                                            (day) => {
                                                                const isSelected =
                                                                    field.value?.includes(
                                                                        day.value
                                                                    )
                                                                return (
                                                                    <Button
                                                                        key={
                                                                            day.value
                                                                        }
                                                                        type="button"
                                                                        variant={
                                                                            isSelected
                                                                                ? 'default'
                                                                                : 'outline'
                                                                        }
                                                                        size="sm"
                                                                        className="h-7 min-w-[36px] px-2 text-xs"
                                                                        onClick={() => {
                                                                            const current =
                                                                                field.value ||
                                                                                []
                                                                            const newVal =
                                                                                isSelected
                                                                                    ? current.filter(
                                                                                          (
                                                                                              v
                                                                                          ) =>
                                                                                              v !==
                                                                                              day.value
                                                                                      )
                                                                                    : [
                                                                                          ...current,
                                                                                          day.value,
                                                                                      ].sort()
                                                                            field.onChange(
                                                                                newVal
                                                                            )
                                                                        }}
                                                                    >
                                                                        {
                                                                            day.label
                                                                        }
                                                                    </Button>
                                                                )
                                                            }
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </div>
                                    </>
                                )}
                            </CardContent>
                        </Card>

                        {/* Custom Terms */}
                        <Card>
                            <CardHeader className="pb-3">
                                <div className="flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-amber-500" />
                                    <CardTitle className="text-sm">
                                        Điều khoản mặc định
                                    </CardTitle>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    {customTermFields.length === 0 ? (
                                        <div className="text-center py-4 bg-gray-50 rounded-lg">
                                            <p className="text-sm text-gray-500 mb-2">
                                                Chưa có điều khoản
                                            </p>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() =>
                                                    appendCustomTerm({
                                                        key: '',
                                                        value: '',
                                                    })
                                                }
                                            >
                                                <Plus className="w-4 h-4 mr-1" />
                                                Thêm
                                            </Button>
                                        </div>
                                    ) : (
                                        <>
                                            {customTermFields.map(
                                                (field, index) => (
                                                    <div
                                                        key={field.id}
                                                        className="space-y-1"
                                                    >
                                                        <div className="flex gap-2 items-center">
                                                            <Controller
                                                                name={`custom_terms.${index}.key`}
                                                                control={
                                                                    control
                                                                }
                                                                render={({
                                                                    field,
                                                                }) => (
                                                                    <Input
                                                                        {...field}
                                                                        className="h-8 flex-1 text-sm"
                                                                        placeholder="Tên điều khoản"
                                                                    />
                                                                )}
                                                            />
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() =>
                                                                    removeCustomTerm(
                                                                        index
                                                                    )
                                                                }
                                                                className="h-8 w-8 p-0 text-red-500"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                        <Controller
                                                            name={`custom_terms.${index}.value`}
                                                            control={control}
                                                            render={({
                                                                field,
                                                            }) => (
                                                                <TextEditorCustomize
                                                                    value={{
                                                                        text:
                                                                            field.value ||
                                                                            '',
                                                                        attachments:
                                                                            [],
                                                                    }}
                                                                    onChange={(
                                                                        val
                                                                    ) =>
                                                                        field.onChange(
                                                                            val.text
                                                                        )
                                                                    }
                                                                    disabledAttachments
                                                                    placeholder="Nội dung điều khoản..."
                                                                />
                                                            )}
                                                        />
                                                    </div>
                                                )
                                            )}
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() =>
                                                    appendCustomTerm({
                                                        key: '',
                                                        value: '',
                                                    })
                                                }
                                                className="w-full"
                                            >
                                                <Plus className="w-4 h-4 mr-1" />
                                                Thêm điều khoản
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>

                {/* Escalation Rules - Full Width */}
                <Card className="mt-2">
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Bell className="w-4 h-4 text-red-500" />
                                <CardTitle className="text-sm">
                                    Cảnh báo SLA
                                </CardTitle>
                            </div>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={handleAddEscalation}
                            >
                                <Plus className="w-4 h-4 mr-1" />
                                Thêm cảnh báo
                            </Button>
                        </div>
                        <CardDescription className="text-xs">
                            Tự động thông báo khi ticket sắp quá hạn SLA
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {escalationFields.length === 0 ? (
                            <div className="text-center py-8 bg-gray-50 rounded-lg">
                                <Bell className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                                <p className="text-sm text-gray-500 mb-2">
                                    Chưa có cảnh báo SLA
                                </p>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={handleAddEscalation}
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Thêm cảnh báo
                                </Button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                {escalationFields.map((field, index) => (
                                    <div
                                        key={field.id}
                                        className="p-3 border rounded-lg bg-gray-50/50 space-y-2"
                                    >
                                        <div className="flex items-center justify-between">
                                            <Badge
                                                variant="outline"
                                                className="text-xs"
                                            >
                                                Cảnh báo #{index + 1}
                                            </Badge>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() =>
                                                    removeEscalation(index)
                                                }
                                                className="h-6 w-6 p-0 text-red-500"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>

                                        <div className="space-y-1">
                                            <Label className="text-xs">
                                                Cảnh báo trước (phút)
                                            </Label>
                                            <Controller
                                                name={`escalation_rules.${index}.condition.no_response_after_minutes`}
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        {...field}
                                                        type="number"
                                                        className="h-8 text-sm"
                                                        onChange={(e) =>
                                                            field.onChange(
                                                                parseInt(
                                                                    e.target
                                                                        .value
                                                                ) || 0
                                                            )
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>

                                        <div className="space-y-1">
                                            <Label className="text-xs">
                                                Áp dụng cho ticket
                                            </Label>
                                            <Controller
                                                name={`escalation_rules.${index}.condition.priority`}
                                                control={control}
                                                render={({ field }) => (
                                                    <div className="flex flex-wrap gap-1">
                                                        {PRIORITY_OPTIONS.map(
                                                            (p) => (
                                                                <Badge
                                                                    key={
                                                                        p.value
                                                                    }
                                                                    variant={
                                                                        field.value?.includes(
                                                                            p.value
                                                                        )
                                                                            ? 'default'
                                                                            : 'outline'
                                                                    }
                                                                    className="cursor-pointer text-xs"
                                                                    onClick={() => {
                                                                        const current =
                                                                            field.value ||
                                                                            []
                                                                        const newVal =
                                                                            current.includes(
                                                                                p.value
                                                                            )
                                                                                ? current.filter(
                                                                                      (
                                                                                          v
                                                                                      ) =>
                                                                                          v !==
                                                                                          p.value
                                                                                  )
                                                                                : [
                                                                                      ...current,
                                                                                      p.value,
                                                                                  ]
                                                                        field.onChange(
                                                                            newVal
                                                                        )
                                                                    }}
                                                                >
                                                                    {p.label}
                                                                </Badge>
                                                            )
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </div>

                                        <div className="space-y-1">
                                            <Label className="text-xs">
                                                Kênh thông báo
                                            </Label>
                                            <Controller
                                                name={`escalation_rules.${index}.notification_channels`}
                                                control={control}
                                                render={({ field }) => (
                                                    <div className="flex flex-wrap gap-1">
                                                        {NOTIFICATION_CHANNELS.map(
                                                            (ch) => {
                                                                const Icon =
                                                                    ch.icon
                                                                const isActive =
                                                                    field.value?.includes(
                                                                        ch.value
                                                                    )
                                                                return (
                                                                    <Button
                                                                        key={
                                                                            ch.value
                                                                        }
                                                                        type="button"
                                                                        variant={
                                                                            isActive
                                                                                ? 'default'
                                                                                : 'outline'
                                                                        }
                                                                        size="sm"
                                                                        className="h-7 text-xs"
                                                                        onClick={() => {
                                                                            const current =
                                                                                field.value ||
                                                                                []
                                                                            const newVal =
                                                                                isActive
                                                                                    ? current.filter(
                                                                                          (
                                                                                              v
                                                                                          ) =>
                                                                                              v !==
                                                                                              ch.value
                                                                                      )
                                                                                    : [
                                                                                          ...current,
                                                                                          ch.value,
                                                                                      ]
                                                                            field.onChange(
                                                                                newVal
                                                                            )
                                                                        }}
                                                                    >
                                                                        <Icon className="w-3 h-3 mr-1" />
                                                                        {
                                                                            ch.label
                                                                        }
                                                                    </Button>
                                                                )
                                                            }
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </div>

                                        <div className="space-y-1">
                                            <Label className="text-xs">
                                                Gửi đến vai trò
                                            </Label>
                                            <Controller
                                                name={`escalation_rules.${index}.escalate_to.roles`}
                                                control={control}
                                                render={({ field }) => (
                                                    <div className="flex flex-wrap gap-1">
                                                        {[
                                                            'manager',
                                                            'team_lead',
                                                            'admin',
                                                        ].map((role) => (
                                                            <Badge
                                                                key={role}
                                                                variant={
                                                                    field.value?.includes(
                                                                        role
                                                                    )
                                                                        ? 'default'
                                                                        : 'outline'
                                                                }
                                                                className="cursor-pointer text-xs capitalize"
                                                                onClick={() => {
                                                                    const current =
                                                                        field.value ||
                                                                        []
                                                                    const newVal =
                                                                        current.includes(
                                                                            role
                                                                        )
                                                                            ? current.filter(
                                                                                  (
                                                                                      v
                                                                                  ) =>
                                                                                      v !==
                                                                                      role
                                                                              )
                                                                            : [
                                                                                  ...current,
                                                                                  role,
                                                                              ]
                                                                    field.onChange(
                                                                        newVal
                                                                    )
                                                                }}
                                                            >
                                                                <Users className="w-3 h-3 mr-1" />
                                                                {role.replace(
                                                                    '_',
                                                                    ' '
                                                                )}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                )}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </form>
        </div>
    )
}

export default SLATemplatePage
